import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  TActorCardColumnKey,
  TActorCardListContext,
  TActorCardViewMode,
  TActorFavoritesViewMode,
  TActorListContext,
  TActorViewMode,
  TCastingListContext,
  TCastingViewMode,
  TFavoriteActorColumnKey,
  TListContext,
  TProjectListContext,
  TProjectViewMode,
  TTableListContext
} from '@/shared/types/ListDisplay';

import {
  ACTOR_CARD_DEFAULT_COLUMNS,
  FAVORITE_ACTOR_DEFAULT_COLUMNS
} from '@/shared/constants';

/**
 * Режим отображения карточек — актёрский, кастинговый, проектный или реестра
 * визиток, в зависимости от контекста экрана. Проектные литералы совпадают с
 * кастинговыми, поэтому объединение от них не расширяется — расширяется только
 * домен КОНТЕКСТОВ, и именно он решает, какой набор разрешён. Визитки объединение
 * всё-таки расширяют: `table` есть только у них.
 */
type TViewMode =
  | TActorCardViewMode
  | TActorViewMode
  | TCastingViewMode
  | TProjectViewMode;

/**
 * Payload `setViewMode` коррелирует `context` и `mode` на уровне типов: актёрскому
 * контексту (`TActorListContext`) разрешён только `TActorViewMode`, кастинговому
 * (`TCastingListContext`) — только `TCastingViewMode`, проектному
 * (`TProjectListContext`) — только `TProjectViewMode`. Присвоение "чужого" режима
 * контексту (например `mode: 'table'` контексту `'actorSearch'`) не компилируется —
 * см. `_tests/src/entities/list-preferences/slice.test.ts` для `@ts-expect-error`
 * проверок этого инварианта.
 *
 * Ветка `'actorFavorites'` — точечное расширение (18976, подготовка под табличный
 * вид избранного): этому ОДНОМУ актёрскому контексту вдобавок разрешён `'table'`
 * (`TActorFavoritesViewMode`). Общая актёрская ветка ниже НЕ сужается через
 * `Exclude<TActorListContext, 'actorFavorites'>` — объединение форм объекта
 * проверяется целиком по каждому члену, и `{ context: 'actorFavorites'; mode:
 * 'photos' }` по-прежнему совпадает с общей веткой; сузив её, `useActorViewMode.ts`
 * (где `context` статически широкий `TActorListContext`) перестал бы
 * компилироваться.
 */
type TSetViewModePayload =
  | { context: TActorCardListContext; mode: TActorCardViewMode }
  | { context: 'actorFavorites'; mode: TActorFavoritesViewMode }
  | { context: TActorListContext; mode: TActorViewMode }
  | { context: TCastingListContext; mode: TCastingViewMode }
  | { context: TProjectListContext; mode: TProjectViewMode };

/**
 * Тип режима отображения, соответствующий типу переданного контекста. Развилка
 * ПЯТИЧАСТНАЯ (18976 добавила ветку `'actorFavorites'`; была четырёхчастная):
 * последняя ветка НЕ «всё остальное — кастинги», иначе проектный контекст молча
 * получал бы кастинговый тип, и разъезд доменов (проекту не нужен пятый вид,
 * реестру визиток нужен `table`) перестал бы проверяться компилятором.
 *
 * Ветка визиток стоит ПЕРВОЙ: её контекст — строковый литерал, и любая более
 * широкая ветка выше перехватила бы его. Ветка `'actorFavorites'` стоит ВТОРОЙ по
 * той же причине — её контекст тоже строковый литерал (один из членов
 * `TActorListContext`), и общая актёрская ветка ниже перехватила бы его первой,
 * если бы стояла раньше, вернув `TActorViewMode` без `'table'`.
 */
type TViewModeForContext<TContext extends TListContext> =
  TContext extends TActorCardListContext
    ? TActorCardViewMode
    : TContext extends 'actorFavorites'
      ? TActorFavoritesViewMode
      : TContext extends TActorListContext
        ? TActorViewMode
        : TContext extends TCastingListContext
          ? TCastingViewMode
          : TProjectViewMode;

/**
 * Набор столбцов, положенный контексту с табличным видом: реестру визиток —
 * `TActorCardColumnKey`, избранному — `TFavoriteActorColumnKey`. Условный тип, а
 * не общий `TActorCardColumnKey | TFavoriteActorColumnKey` на оба контекста: два
 * набора не пересекаются по смыслу (визитка кинокомпании vs актёр из поиска), и
 * общий тип позволил бы записать чужой набор в чужой контекст без ошибки компиляции.
 */
type TTableColumnsForContext<TContext extends TTableListContext> =
  TContext extends TActorCardListContext
    ? TActorCardColumnKey
    : TFavoriteActorColumnKey;

/**
 * Payload `setTableColumns`, коррелирующий `context` и `columns` тем же приёмом,
 * что `TSetViewModePayload`: мапнутый тип, распределённый по членам
 * `TTableListContext`, даёт дискриминированное объединение — присвоить набору
 * реестра столбцы избранного (и наоборот) не получится на этапе компиляции.
 */
type TSetTableColumnsPayload = {
  [TContext in TTableListContext]: {
    context: TContext;
    columns: TTableColumnsForContext<TContext>[];
  };
}[TTableListContext];

/**
 * Дефолтный набор столбцов по контексту — резолвится в СЕЛЕКТОРЕ
 * (`selectTableColumns`), а не в каждом потребителе: у таблицы должен быть
 * рабочий набор с первого рендера что у реестра, что у избранного.
 */
const TABLE_COLUMNS_DEFAULTS: {
  [TContext in TTableListContext]: TTableColumnsForContext<TContext>[];
} = {
  directorActorCards: ACTOR_CARD_DEFAULT_COLUMNS,
  actorFavorites: FAVORITE_ACTOR_DEFAULT_COLUMNS
};

interface IState {
  viewModes: Partial<Record<TListContext, TViewMode>>;
  /**
   * Набор включённых столбцов таблицы — по контекстам с табличным видом
   * (`TTableListContext`): реестру визиток и, с 18976, избранному режиссёра.
   */
  tableColumns: {
    [TContext in TTableListContext]?: TTableColumnsForContext<TContext>[];
  };
}

const initialState: IState = {
  viewModes: {},
  tableColumns: {}
};

// Слайс намеренно НЕ сбрасывается в useLogout при выходе пользователя. Выбранный
// режим отображения карточек — это предпочтение устройства/UI, а не пользовательские
// данные (в отличие от отликов, кастингов, профиля и т.д.), поэтому он должен
// переживать смену аккаунта на одном устройстве. Это осознанное решение дизайн-спеки
// задачи 18170, а не пропуск в чек-листе мультитенантности из CLAUDE.md.
const listPreferencesSlice = createSlice({
  name: 'listPreferences',
  initialState,
  reducers: {
    /**
     * Запоминает выбранный режим отображения карточек для конкретного экрана-списка.
     * @param state Состояние
     * @param action Экшен с контекстом экрана и выбранным режимом отображения
     */
    setViewMode: (state, action: PayloadAction<TSetViewModePayload>) => {
      state.viewModes[action.payload.context] = action.payload.mode;
    },

    /**
     * Запоминает набор включённых столбцов таблицы для экрана-реестра визиток.
     * Пишется полный набор, а не дельта: контрол отдаёт итоговое состояние
     * чекбоксов, и «выключить» столбец нечем, кроме его отсутствия в наборе.
     * @param state Состояние
     * @param action Экшен с контекстом экрана и набором включённых столбцов
     */
    setTableColumns: (
      state,
      action: PayloadAction<TSetTableColumnsPayload>
    ) => {
      if (!state.tableColumns) state.tableColumns = {};

      // Присвоение по ОБОБЩЁННОМУ ключу объекту с типом значения по каждому
      // ключу отдельным TS не выводит (сужение работает при ЧТЕНИИ
      // дискриминированного union, не при индексной ЗАПИСИ) — каст безопасен,
      // потому что payload уже собран строго из TSetTableColumnsPayload на
      // стороне dispatch (см. @ts-expect-error проверки в slice.test.ts)
      (state.tableColumns as Record<TTableListContext, unknown>)[
        action.payload.context
      ] = action.payload.columns;
    }
  }
});

export const { setTableColumns, setViewMode } = listPreferencesSlice.actions;
export const { reducer: listPreferencesReducer } = listPreferencesSlice;

/**
 * Селектор режима отображения карточек для конкретного экрана-списка. Тип результата
 * зависит от типа переданного контекста: `TActorCardListContext` →
 * `TActorCardViewMode`, `TActorListContext` → `TActorViewMode`,
 * `TCastingListContext` → `TCastingViewMode`, `TProjectListContext` →
 * `TProjectViewMode`.
 * @param context Идентификатор экрана-списка
 */
export const selectViewMode =
  <TContext extends TListContext>(context: TContext) =>
  (state: {
    listPreferences: IState;
  }): TViewModeForContext<TContext> | undefined =>
    state.listPreferences.viewModes[context] as
      | TViewModeForContext<TContext>
      | undefined;

/**
 * Селектор набора включённых столбцов таблицы для контекста с табличным видом.
 * На пустом сторе отдаёт дефолтный набор ЭТОГО контекста
 * (`TABLE_COLUMNS_DEFAULTS`) — реестр и избранное держат разные дефолты.
 *
 * `?.` на поле обязателен: слайс приезжает из storage целиком
 * (autoMergeLevel1), и persisted-состояние из версии без этого поля
 * обратилось бы к полю у `undefined`.
 * @param context Контекст с табличным видом
 */
export const selectTableColumns =
  <TContext extends TTableListContext>(context: TContext) =>
  (state: { listPreferences: IState }): TTableColumnsForContext<TContext>[] =>
    (state.listPreferences.tableColumns?.[context] as
      | TTableColumnsForContext<TContext>[]
      | undefined) ?? TABLE_COLUMNS_DEFAULTS[context];
